import { useState, useEffect, useCallback } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import "../../user-settings/components/UserSettingsProvider.js";
import { useColumnsSettings } from "../../user-settings/hooks.js";
import { formatUtcDate } from "../../utils/date.js";
import { updateKeyValueFilterArray } from "../../utils/keyValueFilter.js";
import { maxPageSize } from "../../api/types.js";
import { getFilterString } from "../../api/utils.js";
import "immer";
import "@apollo/client/errors";
import "rxjs";
import "rxjs/operators";
import "@apollo/client";
import "graphql";
import "@opentelemetry/api";
import "../../otel/TraceProvider.js";
import "react-error-boundary";
import "@opentelemetry/context-zone";
import "@opentelemetry/exporter-trace-otlp-http";
import "@opentelemetry/instrumentation";
import "@opentelemetry/instrumentation-document-load";
import "@opentelemetry/instrumentation-xml-http-request";
import "@opentelemetry/resources";
import "@opentelemetry/sdk-trace-web";
import "@opentelemetry/semantic-conventions";
import "@apollo/client/link/subscriptions";
import "@apollo/client/utilities";
import "cross-fetch";
import "graphql-ws";
import "../../node_modules/moment/dist/moment.js";
import "rxjs/ajax";
import { useAuthUser } from "../../auth/hooks.js";
import "oidc-client-ts";
import "../../auto-mapper/AutoMapperProvider.js";
import { useAutoMapper } from "../../auto-mapper/hooks.js";
import { useAsyncActionState } from "../../redux-folder/async-actions/hooks.js";
import "../../redux-folder/async-actions/actions.js";
import "../../intl/shared-resources/serverResponse.js";
import "typesafe-actions";
import "redux-observable";
import "../../redux-folder/async-action/action.js";
import "../../redux-folder/error-handling/action.js";
import "../../redux-folder/actions.js";
import "react-router";
import "../../redux-folder/async-actions/reducers.js";
import "../../redux-folder/download/actions.js";
import "../../redux-folder/download/epics.js";
import { useDownload } from "../../redux-folder/download/hooks.js";
import { useReduxForm, useReduxFormRequestMode } from "../../redux-folder/form/hooks.js";
import { RealtimeMessageTrigger } from "../../redux-folder/realtime/types.js";
import "normalizr";
import "fast-deep-equal/es6";
import "redux";
import "../../redux-folder/entity/epics.js";
import "../../redux-folder/entity/EntityProvider.js";
import "reselect";
import { useViewInit } from "../../redux-folder/view/hooks.js";
import { getNonEmptyValueValidator } from "../../redux-folder/view/utils.js";
import "../../redux-folder/realtime/RealtimeNotification.js";
import { gridUserProfileLocalizedHeaders } from "./components/DataGrid.js";
import { userProfileActions } from "./actions.js";
import { UserProfileViewModelKeysTypeName, UserProfileQueryFieldsTypeName, UserProfileColumnSettingsKeysTypeName, allUserProfileColumnKeys } from "./constants.js";
import { userProfileMappingKeys } from "./mapper.js";
import { AalViewModel } from "./types.js";
function useUserProfiles({
  realtimeMode,
  triggers,
  ids,
  fields,
  viewType,
  displayName,
  defaultFilters,
  defaultPaging,
  defaultSorting,
  hiddenFilters,
  syncWithUrl,
  validateFilter,
  cleanDelay,
  currency
}) {
  const mapper = useAutoMapper();
  const [uids, setUids] = useState(null);
  const {
    items,
    totalCount,
    searchFilter,
    viewEntity: { filter: filterString },
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    handleFilterChange
  } = useViewInit({
    viewType,
    displayName,
    entity: {
      entity: "UserProfile",
      fields: mapFields(fields)
    },
    realtime: realtimeMode && triggers ? {
      entity: "UserProfile",
      mode: realtimeMode,
      triggers: getTriggers()
    } : null,
    defaultSorting: mapSorting(defaultSorting),
    defaultFilters: getDefaultFilters(),
    hiddenFilters,
    defaultPaging: ids?.length > 0 ? { page: 1, pageSize: getUniqueIds().length } : defaultPaging,
    syncWithUrl,
    validateFilter,
    cleanDelay
  });
  function getTriggers() {
    const resultTriggers = triggers.filter((t) => t.type !== RealtimeMessageTrigger.Update);
    const updateTrigger = triggers.find((t) => t.type === RealtimeMessageTrigger.Update);
    if (updateTrigger && uids?.length > 0) {
      resultTriggers.push({
        type: RealtimeMessageTrigger.Update,
        args: updateTrigger.args ?? { filter: { field: "uid", value: uids } }
      });
    }
    return resultTriggers;
  }
  useEffect(() => {
    setUids(items?.map((i) => i.uid));
  }, [items?.map((i) => i.uid).join()]);
  const userItems = items?.map((i) => {
    const revenueShareHistory = mapper.tryMapArray(
      i?.agent_info?.agent_revenue_share_list ?? [],
      userProfileMappingKeys.agentRevenueShare,
      userProfileMappingKeys.agentRevenueShareViewModel
    );
    const aal = mapper.tryMap(i?.aal, Array, AalViewModel);
    const latestRevenueShare = revenueShareHistory?.model?.at(-1);
    return {
      model: {
        ...i,
        id: i.uid,
        currency,
        latest_revenue_share: latestRevenueShare,
        agent_revenue_share_list: revenueShareHistory?.model,
        aal_values: aal?.model
      },
      isModelValid: aal?.isModelValid !== false && revenueShareHistory?.isModelValid !== false,
      rawModelString: JSON.stringify(i)
    };
  });
  return {
    items: userItems?.map((i) => i.model),
    rawItems: userItems?.map((i) => ({ isValid: i.isModelValid, raw: i.rawModelString })),
    totalCount,
    searchFilter,
    filterString,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange: handleVmSortChange,
    handleFilterChange
  };
  function getDefaultFilters() {
    const filters = defaultFilters ? [...defaultFilters] : [];
    if (ids?.length > 0) {
      const uniqueUids = getUniqueIds();
      const uidFilter = uniqueUids.join("|");
      filters.push({ key: "uid", value: uidFilter });
    }
    return filters;
  }
  function getUniqueIds() {
    return [...new Set(ids)];
  }
  function mapFields(fields2) {
    return mapper.map(
      fields2,
      UserProfileViewModelKeysTypeName,
      UserProfileQueryFieldsTypeName
    );
  }
  function mapField(field) {
    return mapFields([field])[0];
  }
  function mapSorting(sorting) {
    return sorting?.map((s) => ({ ...s, field: mapField(s.field) }));
  }
  function handleVmSortChange(sorting) {
    handleSortChange(mapSorting(sorting));
  }
}
function useUserProfileDetails({
  id,
  fields,
  viewType,
  displayName,
  cleanDelay = 0,
  realtimeMode,
  defaultFilters,
  validateFilter,
  isCancelable
}) {
  const mapper = useAutoMapper();
  const queryFields = mapper.map(
    fields,
    UserProfileViewModelKeysTypeName,
    UserProfileQueryFieldsTypeName
  );
  const defaultFilter = updateKeyValueFilterArray(
    [{ key: "uid", value: id ?? null }],
    ...defaultFilters ?? []
  );
  const {
    items,
    searchFilter,
    viewEntity: { filter: filterString, status },
    handleFilterChange
  } = useViewInit({
    viewType,
    entity: {
      entity: "UserProfile",
      fields: queryFields
    },
    realtime: realtimeMode ? {
      entity: "UserProfile",
      mode: realtimeMode,
      triggers: id ? [
        {
          type: RealtimeMessageTrigger.Update,
          args: {
            filter: {
              field: "uid",
              value: [id]
            }
          }
        }
      ] : void 0
    } : null,
    displayName,
    defaultFilters: defaultFilter,
    defaultPaging: { page: 1, pageSize: 1 },
    validateFilter: validateFilter ?? getNonEmptyValueValidator("uid"),
    cleanDelay,
    isCancelable
  });
  let item = items?.length ? items[0] : null;
  if (item) {
    const aal = mapper.tryMap(item?.aal, Array, AalViewModel);
    item = { ...item, aal_values: aal?.model };
  }
  return { item, requestStatus: status, handleFilterChange, searchFilter, filterString };
}
function useCopyReferralLink(uid) {
  const dispatch = useDispatch();
  const { isProgress } = useAsyncActionState(userProfileActions.copyReferralCode, uid);
  function handleCopy() {
    if (!uid) return;
    dispatch(userProfileActions.copyReferralCode.request(uid, { actionId: uid }));
  }
  return { isProgress, handleCopy };
}
function useReferralLinkButton(uid) {
  const [clickedStatus, setClickedStatus] = useState("default");
  const { isProgress, handleCopy } = useCopyReferralLink(uid);
  const resetStatus = useCallback(() => {
    setTimeout(() => {
      setClickedStatus("default");
    }, 3e3);
  }, []);
  function handleClick() {
    setClickedStatus("clicked");
    handleCopy();
    resetStatus();
  }
  return {
    isClicked: clickedStatus === "clicked",
    isProgress,
    handleClick
  };
}
function useUserProfileSettings({
  settingId,
  defaultColumns,
  pinnedColumns = []
}) {
  const { sub } = useAuthUser();
  const mapper = useAutoMapper();
  const { visible, onVisibleColumnsChange } = useColumnsSettings({
    userId: sub,
    settingId,
    defaultColumns: mapViewModelKeysToSettings(defaultColumns),
    pinnedColumns: mapViewModelKeysToSettings(pinnedColumns)
  });
  function mapViewModelKeysToSettings(keys) {
    return [
      ...new Set(
        keys?.map(
          (i) => mapper.map(
            i,
            UserProfileViewModelKeysTypeName,
            UserProfileColumnSettingsKeysTypeName
          )
        )?.filter((i) => i) ?? []
      )
    ];
  }
  function mapSettingsToViewModelKeys(settings) {
    return [
      ...new Set(
        settings?.flatMap(
          (i) => mapper.map(
            i,
            UserProfileColumnSettingsKeysTypeName,
            UserProfileViewModelKeysTypeName
          )
        ) ?? []
      )
    ];
  }
  function handleColumnSettingsChange(fields) {
    const columns = mapViewModelKeysToSettings(fields);
    onVisibleColumnsChange(columns);
  }
  return {
    // filter is workaround for storing multiple keys in single setting key
    visibleColumns: mapSettingsToViewModelKeys(visible).filter((f) => defaultColumns.includes(f)),
    onVisibleColumnsChange: handleColumnSettingsChange
  };
}
function useUserProfileDownload({
  filterString,
  keys,
  filename,
  hiddenKeys = [],
  mode,
  downloadServerStrategyId,
  total
}) {
  const downloadableKeys = keys.filter((i) => !allUserProfileColumnKeys.includes(i));
  const { handleDownload: defaultHandleDownload, isProgress: defaultIsProgress } = useDownload({
    filename,
    filter: filterString,
    mode,
    keys: downloadableKeys,
    total,
    strategySymbol: downloadServerStrategyId,
    headersLocalization: gridUserProfileLocalizedHeaders
  });
  const dispatch = useDispatch();
  const mapper = useAutoMapper();
  const { formatMessage } = useIntl();
  const headers = downloadableKeys?.reduce(
    (previousValue, key) => {
      previousValue[key] = gridUserProfileLocalizedHeaders[key] ? formatMessage(gridUserProfileLocalizedHeaders[key]) : null;
      return previousValue;
    },
    {}
  );
  const { isProgress: customIsProgress } = useAsyncActionState(userProfileActions.download);
  const pageFilterKey = "page";
  const sizeFilterKey = "size";
  const searchFilter = getFilterString(
    filterString,
    true,
    { key: pageFilterKey, value: "1" },
    { key: sizeFilterKey, value: `${maxPageSize}` }
  );
  const fields = mapper.map(
    [...downloadableKeys, ...hiddenKeys],
    UserProfileViewModelKeysTypeName,
    UserProfileQueryFieldsTypeName
  );
  function customHandleClick() {
    if (mode === "client") {
      dispatch(
        userProfileActions.download.request({ filter: searchFilter, fields, filename, worksheetKeys: downloadableKeys, headers })
      );
    }
  }
  return mode === "client" ? { handleDownload: customHandleClick, isProgress: customIsProgress } : { handleDownload: defaultHandleDownload, isProgress: defaultIsProgress };
}
function useAgentRevenueShareUpdate({
  agentId,
  initialModel,
  isMasterAgent
}) {
  const mapper = useAutoMapper();
  const formMethods = useReduxForm({
    initialModel: {
      poker_cash: initialModel?.poker_cash,
      casino_share: initialModel?.casino_share,
      spins: initialModel?.spins,
      sports: initialModel?.sports,
      poker_mtt: initialModel?.poker_mtt,
      commission_subtypes: initialModel?.commission_subtypes
    },
    asyncAction: userProfileActions.updateAgentRevenueShare,
    map: (formModel) => {
      const newValue = {
        ...formModel,
        active_since: formatUtcDate(/* @__PURE__ */ new Date(), "iso-date", false),
        commission_subtypes: isMasterAgent ? formModel.commission_subtypes : void 0
      };
      return {
        agentId,
        agentRevenueShare: mapper.map(
          newValue,
          userProfileMappingKeys.agentRevenueShareViewModel,
          userProfileMappingKeys.agentRevenueShareInput
        )
      };
    },
    mode: "onChange"
  });
  return formMethods;
}
function useUpdateNonWDBalanceForm(userId, amount) {
  const { control, handleSubmit, submit, reset } = useReduxForm({
    asyncAction: userProfileActions.updateNonWDBalance,
    initialModel: {
      amount
    },
    map: (model) => ({
      uid: userId,
      amount: Number(model.amount)
    }),
    shouldResetOnError: false,
    mode: "onChange"
  });
  const { requestMode } = useReduxFormRequestMode(userProfileActions.updateNonWDBalance);
  return {
    control,
    onSubmit: handleSubmit(submit),
    reset,
    requestMode
  };
}
export {
  useAgentRevenueShareUpdate,
  useReferralLinkButton,
  useUpdateNonWDBalanceForm,
  useUserProfileDetails,
  useUserProfileDownload,
  useUserProfileSettings,
  useUserProfiles
};
//# sourceMappingURL=hooks.js.map
